import CoreBase from '../../../services/engines/CoreBase';
import csCodeTemplateEngine from './CsCodeTemplateEngine';

const emptyRef = () => ({ name: '', version: '' });
const emptyProjectRef = () => ({ path: '' });
const emptyFileRef = () => ({ templateRk: '', enabled: true });

/**
 * CsCodeGenFormData — converts between selected config and form state, and builds normalized config.
 * see 87d0d8e0dfc440df88fa0ad2d269faba
 */
class CsCodeGenFormData extends CoreBase {
    _name = 'CsCodeGenFormData';

    /**
     * Builds the default RootFolder path from a HostedService name.
     * @param {string} hostedService
     * @returns {string}
     */
    defaultRootFolder = (hostedService) => {
        const d = csCodeTemplateEngine.getDefaults();
        return `r:\\TestCode\\Kdesk8\\${hostedService || d.hostedServicePlaceholder}\\src`;
    };

    /**
     * Resolves a files list from a selected sub-object, falling back to defaults when missing or
     * when it's still the old pre-20260703 boolean-object shape.
     * @param {*} selectedFiles - selected.plugin.files / selected.data.files / selected.common.files, any shape/version
     * @param {Array} defaultFiles - d.pluginFiles / d.dataFiles / d.commonFiles
     * @returns {Array<{templateRk:string, enabled:boolean}>}
     */
    _resolveFiles = (selectedFiles, defaultFiles) => (Array.isArray(selectedFiles) ? selectedFiles : [...(defaultFiles ?? [])]);

    /**
     * Creates a form state from a selected config.
     * @param {object|null} selected
     * @returns {object}
     */
    fromSelected = (selected) => {
        const d = csCodeTemplateEngine.getDefaults();

        const r = {
            rk: selected?.rk ?? '',
            prefix: selected?.prefix ?? d.prefix,
            projectName: selected?.projectName ?? '',
            pluginName: selected?.pluginName ?? '',
            description: selected?.description ?? d.descriptionPlaceholder,
            remarks: selected?.remarks ?? '',
            author: selected?.author ?? d.author,
            hostedService: selected?.hostedService ?? '',
            rootFolder: selected?.rootFolder ?? '',
            rootFolderEdited: selected?.rootFolderEdited ?? false,

            sharedPackages: selected?.sharedPackages ?? [...d.sharedPackages],
            baseUsings: selected?.baseUsings ?? [...d.baseUsings],
            commonUsings: selected?.commonUsings ?? [...d.commonUsings],

            plugin: {
                enabled: selected?.plugin?.enabled ?? true,
                files: this._resolveFiles(selected?.plugin?.files, d.pluginFiles),
                packageAdditions: selected?.plugin?.packageAdditions ?? [],
                projectReferences: selected?.plugin?.projectReferences ?? [...d.pluginProjectReferences],
            },
            data: {
                enabled: selected?.data?.enabled ?? false,
                files: this._resolveFiles(selected?.data?.files, d.dataFiles),
                packageAdditions: selected?.data?.packageAdditions ?? [...d.dataPackageAdditions],
                projectReferences: selected?.data?.projectReferences ?? [],
            },
            common: {
                enabled: selected?.common?.enabled ?? false,
                files: this._resolveFiles(selected?.common?.files, d.commonFiles),
                packageAdditions: selected?.common?.packageAdditions ?? [],
                projectReferences: selected?.common?.projectReferences ?? [],
            },
        };

        return r;
    };

    /**
     * Returns a default form state for a new CsCodeGen config.
     * @returns {object}
     */
    createDefault = () => {
        const d = csCodeTemplateEngine.getDefaults();
        return this.fromSelected({
            prefix: d.prefix,
            projectName: d.projectNamePlaceholder,
            pluginName: d.pluginNamePlaceholder,
            author: d.author,
            hostedService: d.hostedServicePlaceholder,
            rootFolder: this.defaultRootFolder(d.hostedServicePlaceholder),
            rootFolderEdited: false,
            sharedPackages: [...d.sharedPackages],
            baseUsings: [...d.baseUsings],
            commonUsings: [...d.commonUsings],
        });
    };

    emptyRef = emptyRef;
    emptyProjectRef = emptyProjectRef;
    emptyFileRef = emptyFileRef;

    /**
     * Builds a normalized config object from raw form state.
     * The 3rd Plugin project reference (to the plugin's own Common project) is NOT stored here —
     * CsCodeGenGenerator auto-injects it at generation time, only when common.enabled is true.
     * @param {object} form - raw form state
     * @returns {object} normalized config
     */
    toConfig = (form) => ({
        rk: form.rk,
        prefix: form.prefix.trim() || csCodeTemplateEngine.getDefaults().prefix,
        projectName: form.projectName.trim(),
        pluginName: form.pluginName.trim() || form.projectName.trim(),
        description: form.description ?? '',
        remarks: form.remarks?.trim() ?? '',
        author: form.author?.trim() || csCodeTemplateEngine.getDefaults().author,
        hostedService: form.hostedService.trim(),
        rootFolder: form.rootFolder.trim() || this.defaultRootFolder(form.hostedService.trim()),

        sharedPackages: (form.sharedPackages ?? []).filter((p) => p.name?.trim()),
        baseUsings: (form.baseUsings ?? []).filter((u) => u?.trim()),
        commonUsings: (form.commonUsings ?? []).filter((u) => u?.trim()),

        plugin: {
            enabled: !!form.plugin?.enabled,
            files: (Array.isArray(form.plugin?.files) ? form.plugin.files : []).filter((f) => f.templateRk?.trim()),
            packageAdditions: (form.plugin?.packageAdditions ?? []).filter((p) => p.name?.trim()),
            projectReferences: (form.plugin?.projectReferences ?? []).filter((p) => p.path?.trim()),
        },
        data: {
            enabled: !!form.data?.enabled,
            files: (Array.isArray(form.data?.files) ? form.data.files : []).filter((f) => f.templateRk?.trim()),
            packageAdditions: (form.data?.packageAdditions ?? []).filter((p) => p.name?.trim()),
            projectReferences: (form.data?.projectReferences ?? []).filter((p) => p.path?.trim()),
        },
        common: {
            enabled: !!form.common?.enabled,
            files: (Array.isArray(form.common?.files) ? form.common.files : []).filter((f) => f.templateRk?.trim()),
            packageAdditions: (form.common?.packageAdditions ?? []).filter((p) => p.name?.trim()),
            projectReferences: (form.common?.projectReferences ?? []).filter((p) => p.path?.trim()),
        },
    });
}

const csCodeGenFormData = new CsCodeGenFormData();
export default csCodeGenFormData;
